import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ProviderIntegrationApi } from "../../service/Api/ProviderIntegrationApi";
import { AuthTypeApi } from "../../pages/payerIntegration/types/providerIntegrationtypes";

export type AuthTypeLoadStatus = "idle" | "loading" | "succeeded" | "failed";

export interface AuthTypeState {
  items: AuthTypeApi[];
  status: AuthTypeLoadStatus;
  error: string | null;
}

const initialState: AuthTypeState = {
  items: [],
  status: "idle",
  error: null,
};

type AuthTypeRootSlice = { authType: AuthTypeState };

/**
 * Loads AuthTypes once. Skips if already loading or successfully cached.
 */
export const fetchAuthTypes = createAsyncThunk(
  "authType/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ProviderIntegrationApi.getAuthTypes();
      return Array.isArray(response) ? response : [];
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to load auth types";
      return rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { status } = (getState() as AuthTypeRootSlice).authType;
      return status === "idle" || status === "failed";
    },
  },
);

const authTypeSlice = createSlice({
  name: "authType",
  initialState,
  reducers: {
    clearAuthTypes: () => initialState,
    RESET_STATE: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuthTypes.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAuthTypes.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload;
        state.error = null;
      })
      .addCase(fetchAuthTypes.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          (action.payload as string) || action.error.message || "Request failed";
      });
  },
});

export const { clearAuthTypes } = authTypeSlice.actions;
export default authTypeSlice.reducer;

